import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Play, ArrowUpRight } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import starlineLogo from '@/assets/starline-logo.png';
import { getLivePromotions, recordPromotionClick, promoImageUrl, type Promotion } from '@/services/promotionService';

/**
 * The Star Line Group showcase.
 *
 * Sits below Popular Routes deliberately: the hero's job is to get someone into
 * /search, and a promo panel above the booking path taxes every booking to sell
 * a side business. Here the customer has already seen the core product.
 *
 * Renders nothing at all when there is nothing live — an empty "our partners"
 * heading is worse than no section.
 */
export default function GroupShowcase() {
  const { lang, t } = useLanguage();
  const [promos, setPromos] = useState<Promotion[]>([]);
  const [playing, setPlaying] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getLivePromotions(lang)
      .then(rows => { if (!cancelled) setPromos(rows); })
      .catch(() => { if (!cancelled) setPromos([]); });
    return () => { cancelled = true; };
  }, [lang]);

  if (promos.length === 0) return null;

  return (
    <section className="section-spacing">
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-8 sm:mb-10"
        >
          {/* The group mark sits with the eyebrow so the section reads as
              Star Line's own family of businesses rather than paid placement. */}
          <div className="flex items-center gap-2.5 mb-2 sm:mb-3">
            <img
              src={starlineLogo}
              alt=""
              aria-hidden="true"
              width={28}
              height={28}
              className="w-6 h-6 sm:w-7 sm:h-7 object-contain shrink-0"
            />
            <span className="text-[10px] sm:text-xs font-semibold tracking-widest text-accent-strong uppercase">
              {t('showcase_eyebrow')}
            </span>
          </div>
          <h2 className="font-display text-fluid-h3 font-bold">{t('showcase_title')}</h2>
          <p className="text-fluid-p text-muted-foreground mt-2 max-w-xl">{t('showcase_sub')}</p>
        </motion.div>

        {/* One ratio for the whole row. On phones it is a snap rail you swipe —
            posters are tall, and stacking four of them vertically would bury
            the rest of the page. From md up it becomes a plain grid.
            The negative margins let the rail bleed to the screen edge on
            mobile so a half-visible next card signals "swipe". */}
        <div
          className="
            flex gap-4 sm:gap-5 overflow-x-auto list-scroll snap-x snap-mandatory
            -mx-4 px-4 pb-2
            md:mx-0 md:px-0 md:pb-0 md:overflow-visible
            md:grid md:grid-cols-3 lg:grid-cols-4
          "
        >
          {promos.map((promo, i) => (
            <PromoCard
              key={promo.id}
              promo={promo}
              index={i}
              isPlaying={playing === promo.id}
              onPlay={() => setPlaying(promo.id)}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function PromoCard({
  promo, index, isPlaying, onPlay,
}: { promo: Promotion; index: number; isPlaying: boolean; onPlay: () => void }) {
  const { t } = useLanguage();

  const media = (
    // Fixed 4:5, the same shape for every card in the row, so the rail reserves
    // its space before anything decodes and nothing shifts as images arrive.
    // The ratio is chosen to match Star Line's own poster artwork, so the
    // common case crops nothing at all.
    <div className="relative aspect-[4/5] overflow-hidden rounded-xl bg-secondary/60">
      {isPlaying && promo.youtubeId ? (
        <iframe
          className="absolute inset-0 w-full h-full"
          src={`https://www.youtube-nocookie.com/embed/${promo.youtubeId}?autoplay=1&rel=0&modestbranding=1`}
          title={promo.title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      ) : (
        <>
          <img
            src={promoImageUrl(promo.imageUrl, 320)}
            alt={promo.title}
            loading="lazy"
            decoding="async"
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
          />
          {promo.mediaType === 'youtube' && (
            // Click-to-play. The iframe is not created until the customer asks
            // for it, so no YouTube request — and no third-party cookie — is
            // made just by loading the homepage, and nothing autoplays over a
            // mobile data connection.
            <button
              type="button"
              onClick={onPlay}
              aria-label={`${t('showcase_play')}: ${promo.title}`}
              className="absolute inset-0 flex items-center justify-center bg-black/25 hover:bg-black/35 transition-colors"
            >
              <span className="w-14 h-14 rounded-full bg-white/90 backdrop-blur flex items-center justify-center shadow-lg transition-transform hover:scale-105">
                <Play className="w-6 h-6 text-black ml-0.5" fill="currentColor" aria-hidden="true" />
              </span>
            </button>
          )}
        </>
      )}
    </div>
  );

  const body = (
    <div className="pt-4">
      <h3 className="font-display font-semibold text-base sm:text-lg leading-snug">{promo.title}</h3>
      {promo.subtitle && (
        <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed mt-1.5">{promo.subtitle}</p>
      )}
      {promo.ctaUrl && promo.ctaLabel && (
        <span className="inline-flex items-center gap-1.5 mt-3 text-sm font-medium text-primary">
          {promo.ctaLabel}
          <ArrowUpRight className="w-4 h-4" aria-hidden="true" />
        </span>
      )}
    </div>
  );

  const isExternal = promo.ctaUrl?.startsWith('http');

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.08 }}
      className="
        glass-card-elevated p-4 sm:p-5 card-hover group
        snap-start shrink-0 w-[68%] sm:w-[42%]
        md:w-auto md:shrink
      "
    >
      {media}
      {promo.ctaUrl ? (
        <a
          href={promo.ctaUrl}
          // Counting a click must never delay or break the navigation, so this
          // is fire-and-forget and the anchor behaves normally.
          onClick={() => { void recordPromotionClick(promo.id); }}
          {...(isExternal
            // noopener/noreferrer: without them the destination gets a handle on
            // this window via window.opener and can navigate it away.
            ? { target: '_blank', rel: 'noopener noreferrer' }
            : {})}
          className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 rounded-lg"
        >
          {body}
        </a>
      ) : (
        body
      )}
    </motion.div>
  );
}
